import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { dashTabQueryKey } from '@entities/dashboard/model/queries';
import { EventJournalQueries } from '@entities/eventJournal/model/queries';
import { useResetFilters } from '@features/JournalEventsFilters/lib';
import { deleteSearchParams, snakeToCamel } from '@shared/helpers';
// NOTE в идеале это надо хранить в entity, не нужно выносить это в shared
import { useNuqsQueryState } from '@shared/hooks';
import { folderIdQueryParam } from '@shared/model/query-params';
import { EventJournal, Files } from '@widgets';

import { getDashboard } from '../../store/slices/dashboardSlice/action';
import { DashboardInformation } from '../../TemporaryConmonents/DashboardInformation/DashboardInformation';
import { useDashboardInformation } from '../../TemporaryConmonents/DashboardInformation/useDashboardInformation';
import { Measurements } from '../../TemporaryConmonents/Measurements/Measurements';

const list = {
    information: {
        title: i18next.t('useDashboard.return.list.information'),
        value: 'info',
    },
    measurements: {
        title: i18next.t('useDashboard.return.list.measurements'),
        value: 'measurements',
    },
    files: {
        title: i18next.t('useDashboard.return.list.files'),
        value: 'files',
    },
    eventJournal: {
        title: i18next.t('useDashboard.return.list.EventJournal'),
        value: 'eventJournal',
    },
};

interface IPresentationState {
    isOpen: boolean;
    header: string;
    subheader: string;
    videoSrc: string;
    tariffQuery: string;
}

interface UseDashboardParams {
    projectId?: number;
}

export const useDashboard = ({ projectId }: UseDashboardParams = {}) => {
    const { state: activeTab, setState: setActiveTab } = useNuqsQueryState(dashTabQueryKey);
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const [presentationState, setPresentationState] = useState<IPresentationState>({
        isOpen: false,
        header: '',
        subheader: '',
        videoSrc: '',
        tariffQuery: '',
    });

    const onClosePresentation = () => {
        setPresentationState({
            isOpen: false,
            header: '',
            subheader: '',
            videoSrc: '',
            tariffQuery: '',
        });
    };

    const {
        isLoading,
        tabs,
        needFetchData,
        information,
        is_owner,
        projectId: currentProjectId,
    } = useDashboardInformation();
    const { onResetFilters } = useResetFilters();

    const resetRef = useRef(onResetFilters);
    useEffect(() => {
        resetRef.current = onResetFilters;
    }, [onResetFilters]);

    useEffect(() => {
        deleteSearchParams(folderIdQueryParam);
        deleteSearchParams(EventJournalQueries.sort);
    }, []);

    useEffect(() => {
        if (needFetchData || (projectId && currentProjectId !== projectId)) {
            dispatch(getDashboard());
        }
    }, [currentProjectId, dispatch, needFetchData, projectId]);

    useEffect(() => {
        if (!activeTab) void setActiveTab('info');
    }, [activeTab, setActiveTab]);

    const onTabChange = useCallback(
        (value: string) => {
            if (value === activeTab) return;
            if (value === 'eventJournal' && tabs.data.event_journal.view_presentation) {
                setPresentationState({
                    isOpen: true,
                    header: t('useDashboard.onTabChange.setPresentationState.header'),
                    subheader: t('useDashboard.onTabChange.setPresentationState.subheader'),
                    videoSrc: t('useDashboard.onTabChange.setPresentationState.videoSrc'),
                    tariffQuery: '?tk=tariff_can_use_event_journal&tv=tariff_can_use_event_journal_yes',
                });
                return;
            }

            setActiveTab(value);
            deleteSearchParams(folderIdQueryParam);
            deleteSearchParams(EventJournalQueries.sort);
            onResetFilters();
        },
        [onResetFilters, activeTab]
    );

    const tabsContentMapping: Record<string, ReactNode> = useMemo(
        () => ({
            info: <DashboardInformation />,
            measurements: <Measurements />,
            files: <Files />,
            eventJournal: <EventJournal isOwner={is_owner} />,
        }),
        [is_owner]
    );

    const [tabsList, setTabsList] = useState([]);

    const changeTariff = async () => {
        // @ts-ignore
        const url = routable.getRoute('openChangeTariff');
        if (is_owner) {
            // @ts-ignore
            $.modalable('open', {
                url,
            });
        } else {
            // @ts-ignore
            $.modalable('open', {
                content: `<div>${t('useDashboard.return.changeTariff.content')}</div>`,
            });
        }
    };

    useEffect(() => {
        // @ts-ignore
        const newList = [];
        Object.entries(tabs.data).forEach(([name, value]) => {
            // @ts-ignore
            const listItem = list[snakeToCamel(name)];
            // @ts-ignore
            if (!value.access_tariff && !value.view_presentation) {
                listItem.action = changeTariff;
            }
            // @ts-ignore
            if (value.view) {
                newList.push(listItem);
            }
        });
        // newList.push(list.eventJournal);
        // @ts-ignore
        setTabsList(newList);
    }, [isLoading, information]);

    return {
        tabsList,
        activeTab,
        onTabChange,
        isLoading,
        tabsContentMapping,
        information,
        tabs,
        presentationState,
        onClosePresentation,
    };
};
